import algoliasearch from 'algoliasearch';
import aa from "search-insights";
import shopify from "../../shopify";
import cloneDeep from 'lodash/cloneDeep';

// Make aa globally available
window.aa = aa;

const DOMAIN = 'mj-bale-int-dev.myshopify.com';
const algoliaFacetOrder = null;

// Global settings
const configuredFacets = JSON.parse(algoliaFacetOrder);
const enabledFacets = configuredFacets ? configuredFacets.filter(facet => facet.enabled).map(facet => facet.name) : [];
let usedFacets = [];
let indexes = [];
const sortedFacets = enabledFacets.filter(facet => usedFacets.includes(facet));

const defaultSearchParams = {
    hitsPerPage: 0,
    distinct: 1,
    facetingAfterDistinct: true,
    attributesToHighlight: [],
    attributesToRetrieve: ['-*', 'id', 'query'],
    facets: sortedFacets,
    // Analytics tracking parameter for all product requests
    clickAnalytics: true,
};

// Default search parameters for different content types
const searchParamsByType = {
    products: {
        ...defaultSearchParams,
        hitsPerPage: 20,
        attributesToRetrieve: ['id', 'title', 'handle', 'vendor', 'product_type', 'tags', 'variants', 'images', 'price', 'price_min', 'price_max', 'available'],
        facets: ['vendor', 'product_type', 'tags', 'price_range', 'inventory_available']
    },
    articles: {
        ...defaultSearchParams,
        hitsPerPage: 5,
        attributesToRetrieve: ['id', 'title', 'handle', 'content', 'summary', 'author', 'tags', 'created_at', 'image'],
        facets: ['tags', 'author']
    },
    categories: {
        ...defaultSearchParams,
        hitsPerPage: 10,
        attributesToRetrieve: ['id', 'title', 'handle', 'description'],
        facets: []
    },
    suggestions: {
        ...defaultSearchParams,
        hitsPerPage: 8,
        attributesToRetrieve: ['id', 'title', 'query'],
        facets: []
    }
};

export const algolia = {
    name: 'algolia',
    component() {
        return {
            open: false,
            config: {},
            client: null,
            isProcessingRequest: false,
            hasInit: false,
            requestType: '',
            currentRequest: null,
            headerResultsTotal: null,
            productsPerPageSetting: null,
            defaultSort: null,
            requestCountPromise: null,
            insightIndex: '',
            requestCountResolve: null,
            isFiltering: false,
            currentQueryID: null,

            async init(settings, productsPerPage, headerResultsTotal) {
                if (this.hasInit) return;

                if (settings) {
                    usedFacets = settings?.facets?.replaceAll("\n", "").split(",");
                    defaultSearchParams.attributesToRetrieve = settings?.attributes?.replaceAll("\n", "").split(",");

                    this.productsPerPageSetting = productsPerPage;
                    this.headerResultsTotal = settings?.results_returned_header;

                    indexes = settings?.search_indexes?.split(',').reduce((acc, pair) => {
                        const [key, value] = pair.split(':').map(str => str.trim());
                        acc[key] = value;
                        return acc;
                    }, {});
                    
                    this.config = {
                        app: settings.app_id || null,
                        key: settings.api_key || null,
                        hide_out_of_stock: settings.filters_hide_out_of_stock,
                        index: indexes,
                    };

                    this.defaultSort = settings?.default_sort;
                    this.client = algoliasearch(this.config.app, this.config.key);
                    this.hasInit = true;

                    aa('init', {
                        appId: settings.app_id,
                        apiKey: settings.api_key,
                        useCookie: false,
                    });

                    this.insightIndex = settings?.insight_index;
                    this.sortOptions = settings?.sort_indicies?.split(',').map(pair => {
                        const [key, value] = pair.split(':').map(str => str.trim());
                        return { key, value };
                    });

                    window.sortOptions = cloneDeep(this.sortOptions);
                    window.defaultSort = cloneDeep(this.defaultSort);

                    // Updated event listeners for unified search
                    window.addEventListener('searchrequest', this.handleUnifiedSearchRequest.bind(this));
                    window.addEventListener('searchrequest-filter-applied', this.handleUnifiedSearchRequest.bind(this));
                    window.addEventListener('searchrequest-sort-applied', this.handleUnifiedSearchRequest.bind(this));
                    window.addEventListener('searchrequest-nextpage', this.handlePaginationRequest.bind(this));
                    window.addEventListener('insight-add', this.handleAddToCartInsight.bind(this));
                    window.addEventListener('product-click', this.handleProductClickEvent.bind(this));
                    
                    // Legacy event listeners for backward compatibility
                    window.addEventListener('searchrequest-articles', this.handleUnifiedSearchRequest.bind(this));
                    window.addEventListener('searchrequest-suggestions', this.handleUnifiedSearchRequest.bind(this));
                    
                    window.algoliaProductClick = this.handleProductClick.bind(this);
                }
            },

            /**
             * Unified multi-index search request handler
             * Combines products, articles, categories, and suggestions into a single API call
             */
            async handleUnifiedSearchRequest(event) {
                const { query, type, filters, index, productsPerPage, isFiltering, searchOptions = {} } = event.detail;

                if (this.isProcessingRequest) {
                    this.cancelCurrentRequest();
                }

                this.isProcessingRequest = true;
                this.requestType = type;

                if (isFiltering) {
                    this.isFiltering = isFiltering;
                }

                // Show spinner for appropriate request types
                if (this.requestType === 'search') {
                    window.dispatchEvent(new CustomEvent('show-spinner', {
                        detail: { id: 'spinner-search' },
                    }));
                } else if (this.requestType === 'plp') {
                    window.dispatchEvent(new CustomEvent('show-spinner', {
                        detail: { id: 'spinner-collection' },
                    }));
                }

                // Use the same search index logic for both header and main search
                const searchIndex = this.config.index[index] ? index : this.defaultSort;

                // Set hits per page based on request type
                if (this.requestType === 'header') {
                    defaultSearchParams.hitsPerPage = this.headerResultsTotal;
                } else {
                    defaultSearchParams.hitsPerPage = productsPerPage
                        ? productsPerPage
                        : this.productsPerPageSetting;
                }

                try {
                    // Build unified multi-index search queries
                    const queries = this.buildMultiIndexQueries(query, filters, searchIndex, productsPerPage, searchOptions);
                    
                    // Execute single multi-index search request
                    const results = await this.executeMultiIndexSearch(queries);
                    
                    // Process and dispatch results
                    await this.processMultiIndexResults(results, query, type);
                    
                } catch (error) {
                    console.error('Unified search request failed:', error);
                    this.dispatchErrorEvent(error);
                } finally {
                    this.isProcessingRequest = false;
                    window.dispatchEvent(new CustomEvent('loading', {
                        detail: { value: false },
                    }));
                }
            },

            /**
             * Build queries for multi-index search
             */
            buildMultiIndexQueries(query, filters, index, productsPerPage, searchOptions = {}) {
                const queries = [];
                const customSearchParams = this.buildCustomSearchParams(filters);

                // Determine which indexes to search based on request type and options
                const indexesToSearch = this.determineIndexesToSearch(index, searchOptions);

                indexesToSearch.forEach(({ indexKey, contentType }) => {
                    const indexName = this.config.index[indexKey];
                    if (!indexName) return;

                    // Get base search parameters for content type
                    const baseParams = cloneDeep(searchParamsByType[contentType] || searchParamsByType.products);
                    
                    // Apply custom parameters
                    const searchParams = {
                        ...baseParams,
                        ...customSearchParams,
                        facets: usedFacets.length ? usedFacets : baseParams.facets,
                    };

                    // Adjust hits per page based on content type and request
                    if (contentType === 'products') {
                        if (this.requestType === 'header') {
                            searchParams.hitsPerPage = this.headerResultsTotal || 5;
                        } else {
                            searchParams.hitsPerPage = productsPerPage || this.productsPerPageSetting || 20;
                        }
                    }

                    queries.push({
                        indexName,
                        query: query || '',
                        params: searchParams,
                        contentType,
                        indexKey
                    });
                });

                return queries;
            },

            /**
             * Determine which indexes to search based on request type and options
             */
            determineIndexesToSearch(index, searchOptions = {}) {
                const indexes = [];

                // For sort operations, only search the specific sorted index for products
                if (index && this.config.index[index]) {
                    const contentType = this.getContentTypeForIndexKey(index);
                    indexes.push({ indexKey: index, contentType });
                    
                    // For multi-index, also include other content types with their default indexes
                    if (!searchOptions.contentTypes || searchOptions.contentTypes.length > 1) {
                        Object.keys(this.config.index).forEach(indexKey => {
                            const otherContentType = this.getContentTypeForIndexKey(indexKey);
                            // Only add if it's a different content type and not already added
                            if (otherContentType !== contentType && 
                                !indexes.some(idx => idx.contentType === otherContentType)) {
                                indexes.push({ indexKey, contentType: otherContentType });
                            }
                        });
                    }
                } else if (searchOptions.contentTypes) {
                    // Search only specified content types
                    searchOptions.contentTypes.forEach(contentType => {
                        const indexKey = this.getIndexKeyForContentType(contentType, index);
                        if (indexKey && this.config.index[indexKey]) {
                            indexes.push({ indexKey, contentType });
                        }
                    });
                } else {
                    // Default: search all available indexes
                    Object.keys(this.config.index).forEach(indexKey => {
                        const contentType = this.getContentTypeForIndexKey(indexKey);
                        indexes.push({ indexKey, contentType });
                    });
                }

                // console.log('Algolia - determined indexes to search:', indexes);
                return indexes;
            },

            /**
             * Get content type from index key
             */
            getContentTypeForIndexKey(indexKey) {
                if (indexKey.includes('article')) return 'articles';
                if (indexKey.includes('category') || indexKey.includes('collection')) return 'categories';
                if (indexKey.includes('suggestion') || indexKey.includes('query')) return 'suggestions';
                return 'products';
            },

            /**
             * Get index key for content type
             */
            getIndexKeyForContentType(contentType, preferredIndex) {
                if (preferredIndex && this.config.index[preferredIndex]) {
                    return preferredIndex;
                }

                // Map content types to likely index keys
                const typeToIndexMap = {
                    products: this.defaultSort || 'default',
                    articles: 'articles',
                    categories: 'categories',
                    suggestions: 'suggestions'
                };

                return typeToIndexMap[contentType] || this.defaultSort;
            },

            /**
             * Execute multi-index search with single API call
             */
            async executeMultiIndexSearch(queries) {
                try {
                    console.log('Executing unified multi-index search:', queries.length, 'queries');
                    
                    // Use Algolia's multipleQueries for optimal performance
                    const searchQueries = queries.map(({ indexName, query, params }) => ({
                        indexName,
                        query,
                        params
                    }));

                    const algoliaResults = await this.client.multipleQueries(searchQueries);
                    
                    // Enhance results with metadata
                    algoliaResults.results = algoliaResults.results.map((result, index) => ({
                        ...result,
                        contentType: queries[index].contentType,
                        indexKey: queries[index].indexKey
                    }));

                    return algoliaResults;
                } catch (error) {
                    console.error('Multi-index search failed:', error);
                    throw error;
                }
            },

            /**
             * Process and dispatch multi-index search results
             */
            async processMultiIndexResults(algoliaResults, query, requestType) {
                const resultsByType = {
                    products: [],
                    articles: [],
                    categories: [],
                    suggestions: []
                };

                let totalResults = 0;
                let filters = null;
                let pages = 1;
                let queryID = null;

                // Process each result set
                for (const result of algoliaResults.results) {
                    const { contentType } = result;
                    
                    // Capture queryID from any result (typically from products)
                    if (result.queryID && !queryID) {
                        queryID = result.queryID;
                        this.currentQueryID = queryID;
                        window.algoliaQueryID = queryID;
                    }

                    if (contentType === 'products') {
                        // Transform products using existing method
                        const transformedProducts = await this.transformProducts(result);
                        resultsByType.products = transformedProducts.products;
                        totalResults = transformedProducts.total_results || result.nbHits;
                        filters = transformedProducts.filters;
                        pages = transformedProducts.pages || result.nbPages;
                    } else {
                        // For other content types, use hits directly
                        resultsByType[contentType] = result.hits || [];
                        if (contentType === 'articles') {
                            totalResults += result.nbHits || 0;
                        }
                    }
                }

                // Dispatch unified results event
                window.dispatchEvent(new CustomEvent('searchrequest-unified-complete', {
                    detail: {
                        query,
                        type: requestType,
                        queryID,
                        totalResults,
                        pages,
                        filters,
                        isFiltering: this.isFiltering,
                        results: resultsByType
                    },
                }));

                // Dispatch backward-compatible events
                this.dispatchLegacyEvents(resultsByType, query, requestType, totalResults, filters, pages, queryID);

                console.log('Unified multi-index search completed:', resultsByType);
            },

            /**
             * Dispatch legacy events for backward compatibility
             */
            dispatchLegacyEvents(resultsByType, query, requestType, totalResults, filters, pages, queryID) {
                // Always dispatch products event (even with empty results)
                window.dispatchEvent(new CustomEvent('searchrequest-products-complete', {
                    detail: {
                        query,
                        products: resultsByType.products,
                        totalResults: resultsByType.products.length > 0 ? totalResults : 0,
                        filters,
                        type: requestType,
                        page: 0, // Will be updated by pagination
                        isFiltering: this.isFiltering,
                        queryID
                    },
                }));

                // Always dispatch articles event (even with empty results)
                window.dispatchEvent(new CustomEvent('searchrequest-articles-complete', {
                    detail: {
                        articles: resultsByType.articles,
                        totalResults: resultsByType.articles.length,
                        type: requestType,
                    },
                }));

                // Always dispatch suggestions event - only real data from Algolia
                const suggestionData = { type: requestType };
                
                // Only use real suggestions from Algolia indexes
                suggestionData.suggestions = resultsByType.suggestions || [];
                suggestionData.categories = resultsByType.categories || [];

                window.dispatchEvent(new CustomEvent('searchrequest-suggestions-complete', {
                    detail: suggestionData,
                }));
            },

            /**
             * Dispatch error event
             */
            dispatchErrorEvent(error) {
                window.dispatchEvent(new CustomEvent('searchrequest-error', {
                    detail: {
                        error: error.message || 'Search request failed',
                        type: this.requestType
                    },
                }));
            },

            // Legacy methods maintained for backward compatibility
            async handleSearchRequest(event) {
                // Redirect to unified search
                await this.handleUnifiedSearchRequest(event);
            },

            async handleSuggestionSearchRequest(event) {
                // Redirect to unified search with suggestions focus
                const modifiedEvent = {
                    ...event,
                    detail: {
                        ...event.detail,
                        searchOptions: {
                            contentTypes: ['suggestions', 'categories']
                        }
                    }
                };
                await this.handleUnifiedSearchRequest(modifiedEvent);
            },

            async handleFilterRequest(event) {
                const { query, type, filters, index, isFilteringRequest } = event.detail;

                this.requestCountPromise = new Promise((resolve) => {
                    this.requestCountResolve = resolve;
                });

                if (this.isProcessingRequest) {
                    this.cancelCurrentRequest();
                }
                this.isProcessingRequest = true;
                this.requestType = type;

                this.isFiltering = isFilteringRequest;

                const searchIndex = this.config.index[index] ? index : this.defaultSort;

                if (this.requestType === 'search') {
                    window.dispatchEvent(new CustomEvent('show-spinner', {
                        detail: {
                            id: 'spinner-search',
                        },
                    }));
                } else if (this.requestType === 'plp') {
                    window.dispatchEvent(new CustomEvent('show-spinner', {
                        detail: {
                            id: 'spinner-collection',
                        },
                    }));
                }
                const customSearchParams = this.buildCustomSearchParams(filters);
                await this.algoliaProductSearch(query, customSearchParams, {}, searchIndex, true);
            },

            getAnonymousUserToken() {
                const tokenKey = 'uuid';
                let userToken = localStorage.getItem(tokenKey);
            
                if (!userToken) {
                    userToken = crypto.randomUUID();
                    localStorage.setItem(tokenKey, userToken);
                }
            
                return userToken;
            },

            async handleAddToCartInsight(event) {
                try {
                    const response = await fetch(`/cart.js`);
                    const cartData = await response.json();

                    const objectIDs = cartData.items.map(item => item.id.toString());
                    const objectData = cartData.items.map(item => ({
                        price: item.original_price / 100,
                        discount: item.original_line_price > item.final_line_price
                            ? (item.original_line_price - item.final_line_price) / 100
                            : 0,
                        quantity: item.quantity
                    }));

                    const value = objectData.reduce((sum, item) => sum + item.price * item.quantity, 0);
               
                    // Track add to cart event
                    aa('addedToCartObjectIDs', {
                        userToken: this.getAnonymousUserToken(),
                        index: this.insightIndex,
                        eventName: 'addedToCartObjectIDs',
                        objectIDs,
                        objectData,
                        value,
                        currency: cartData?.currency,
                        ...(event.detail.customer_id && { authenticatedUserToken: `${event.detail.customer_id}` })
                    });

                    // Track conversion event using the correct method for conversions
                    // Use convertedObjectIDsAfterSearch if we have a queryID, otherwise use general conversion tracking
                    if (this.currentQueryID) {
                        aa('convertedObjectIDsAfterSearch', {
                            userToken: this.getAnonymousUserToken(),
                            index: this.insightIndex,
                            eventName: 'Add to Cart Conversion After Search',
                            queryID: this.currentQueryID,
                            objectIDs,
                            ...(event.detail.customer_id && { authenticatedUserToken: `${event.detail.customer_id}` })
                        });
                    } else {
                        // For direct product page conversions without search context
                        aa('convertedObjectIDs', {
                            userToken: this.getAnonymousUserToken(),
                            index: this.insightIndex,
                            eventName: 'Add to Cart Conversion',
                            objectIDs,
                            ...(event.detail.customer_id && { authenticatedUserToken: `${event.detail.customer_id}` })
                        });
                    }

                } catch (error) {
                    console.error("Error fetching cart:", error);
                }
            },

            handleProductClick(productId, productTitle, productUrl, queryID) {
                const clickQueryID = queryID || this.currentQueryID;
                
                if (clickQueryID && productId) {
                    aa('clickedObjectIDsAfterSearch', {
                        userToken: this.getAnonymousUserToken(),
                        index: this.insightIndex,
                        eventName: 'Product Clicked',
                        queryID: clickQueryID,
                        objectIDs: [productId.toString()],
                        positions: [1]
                    });
                    
                    console.log('Product click tracked:', {
                        productId,
                        productTitle,
                        productUrl,
                        queryID: clickQueryID
                    });
                } else {
                    console.warn('Product click not tracked - missing queryID or productId', {
                        productId,
                        queryID: clickQueryID
                    });
                }
            },

            handleProductClickEvent(event) {
                const { productId, productTitle, productUrl, queryID } = event.detail;
                this.handleProductClick(productId, productTitle, productUrl, queryID);
            },

            buildCustomSearchParams(filters, page) {
                let customSearchParams = {};
                if (filters) {
                    if (filters.collection) {
                        customSearchParams.filters = `collections:${filters.collection}`;
                    } else {
                        if (this.config.hide_out_of_stock) {
                            customSearchParams.filters = filters + ` AND inventory_available:true`;
                        } else {
                            customSearchParams.filters = filters;
                        }
                    }
                }
                if (page) {
                    customSearchParams.page = page;
                }
                return customSearchParams;
            },

            cancelCurrentRequest() {
                if (this.currentRequest && typeof this.currentRequest.cancel === 'function') {
                    this.currentRequest.cancel();
                }
                this.isProcessingRequest = false;
            },

            async transformProducts(algoliaResults) {
                const productIdsList = [...new Set(algoliaResults.hits.map(product => product.id))];
                const transformedProducts = await shopify.getShopifyProductList(productIdsList);
                const output = {
                    products: transformedProducts,
                    page: algoliaResults.page,
                    filters: null,
                    total_results: null,
                    pages: null,
                };

                if (algoliaResults.facets) {
                    output.filters = this.transformFacets(algoliaResults.facets);
                }

                if (algoliaResults.nbHits > -1) {
                    output.total_results = algoliaResults.nbHits;
                }

                if (algoliaResults.nbPages > -1) {
                    output.pages = algoliaResults.nbPages;
                }

                return output;
            },

            transformFacets(facets) {
                let acc = []

                return Object.entries(facets).reduce((accumulator, [facetKey, facetValues]) => {
                    let facetLabel = facetKey.split('.').pop();
                    if (facetLabel === 'inventory_available') {
                        facetLabel = 'availability';
                    } else if (facetLabel === 'price_range') {
                        facetLabel = 'price';
                    }

                    const values = Object.entries(facetValues).reduce((acc, [valueKey, valueCount]) => {
                        let valueLabel = valueKey;
                        if (valueLabel === 'true') {
                            valueLabel = 'available';
                        } else if (valueLabel === 'false') {
                            valueLabel = 'unavailable';
                        }
                        acc.push({ key: valueKey, label: valueLabel, count: valueCount });
                        return acc;
                    }, []).sort((a, b) => {
                        if (facetLabel === 'price') {
                            return parseFloat(a.label.split(':')[0]) - parseFloat(b.label.split(':')[0]);
                        }
                        return a.label.localeCompare(b.label);
                    });

                    acc.push({ label: facetLabel, key: facetKey, values });
                    return acc;
                }, []).sort((a, b) => sortedFacets.indexOf(a.key) - sortedFacets.indexOf(b.key));
            },

            // Legacy individual search methods maintained for backward compatibility
            async algoliaProductSearch(searchQuery = '', customSearchParams = {}, requestOptions = {}, indexKey) {
                const index = this.client.initIndex(this.config.index[indexKey]);
                const searchParams = {
                    ...defaultSearchParams,
                    facets: usedFacets,
                    ...customSearchParams,
                };

                try {
                    const algoliaResults = await index.search(searchQuery, searchParams, requestOptions);
                    
                    if (algoliaResults.queryID) {
                        this.currentQueryID = algoliaResults.queryID;
                        window.algoliaQueryID = algoliaResults.queryID;
                    }
                    
                    const results = await this.transformProducts(algoliaResults);

                    window.dispatchEvent(new CustomEvent('searchrequest-products-complete', {
                        detail: {
                            query: searchQuery,
                            products: results.products,
                            totalResults: results.total_results,
                            filters: results.filters,
                            type: this.requestType,
                            page: results.page,
                            isFiltering: this.isFiltering,
                            queryID: this.currentQueryID
                        },
                    }));
                    console.log('Algolia search finished', results);
                    this.isProcessingRequest = false;
                    return results;
                } catch (err) {
                    console.log('ERROR: ', err);
                    this.isProcessingRequest = false;
                    return null;
                }
            },

            async algoliaArticleSearch(searchQuery = '', customSearchParams = {}, requestOptions = {}, indexKey) {
                const index = this.client.initIndex(this.config.index[indexKey]);
                const searchParams = {
                    ...defaultSearchParams,
                    facets: usedFacets,
                    ...customSearchParams,
                };
                try {
                    const algoliaResults = await index.search(searchQuery, searchParams, requestOptions);

                    window.dispatchEvent(new CustomEvent('searchrequest-articles-complete', {
                        detail: {
                            articles: algoliaResults.hits,
                            totalResults: algoliaResults.nbHits,
                            type: this.requestType,
                        },
                    }));
                    this.isProcessingRequest = false;
                    return algoliaResults;
                } catch (err) {
                    console.log('ERROR: ', err);
                    this.isProcessingRequest = false;
                    return null;
                }
            },

            async algoliaSuggestionsSearch(queries) {
                try {
                    const eventDetails = {
                        type: this.requestType,
                    };
                    const algoliaResults = await this.client.multipleQueries(queries);
                    Object.keys(this.config.index).forEach((key) => {
                        const indexName = this.config.index[key];
                        const matchingResults = algoliaResults.results.find((result) => result.index.includes(indexName))?.hits || [];
                        if (matchingResults.length > 0) {
                            eventDetails[key] = matchingResults;
                        }
                    });
                    window.dispatchEvent(new CustomEvent('searchrequest-suggestions-complete', {
                        detail: { ...eventDetails },
                    }));
                    this.isProcessingRequest = false;
                    return algoliaResults;
                } catch (err) {
                    console.log('ERROR: ', err);
                    this.isProcessingRequest = false;
                    return null;
                }
            },

            async algoliaGenericSearch(searchQuery = '', customSearchParams = {}, requestOptions = {}, indexKey) {
                const index = this.client.initIndex(this.config.index[indexKey]);
                const searchParams = {
                    ...defaultSearchParams,
                    facets: ['vendor', 'meta.information.range'],
                    ...customSearchParams,
                };

                try {
                    const algoliaResults = await index.search(searchQuery, searchParams, requestOptions);
                    return algoliaResults;
                } catch (err) {
                    console.log('ERROR: ', err);
                    return null;
                }
            },

            async algoliaGetProduct(id, indexKey) {
                const index = this.client.initIndex(this.config.index[indexKey]);
                const searchParams = { ...defaultSearchParams };

                try {
                    const result = await index.search(`${id}`, searchParams, {});
                    if (!result.hits) return null;
                    return result.hits[0].objectID;
                } catch (err) {
                    console.log('ERROR: ', err);
                    return null;
                }
            },

            async algoliaGetRelatedProducts(objectID, customSearchParams = {}, indexKey, count) {
                const indexName = config.index[indexKey];
                const searchParams = { ...customSearchParams };

                try {
                    const relatedProducts = await recommendClient.getRelatedProducts([
                        {
                            indexName,
                            objectID,
                            maxRecommendations: count,
                            queryParameters: searchParams,
                        },
                    ]);
                    if (!relatedProducts.length || !relatedProducts[0].hits) return null;
                    return await this.transformProducts(relatedProducts[0]);
                } catch (err) {
                    console.log('ERROR: ', err);
                    return null;
                }
            },

            async handlePaginationRequest(event) {
                const { query, type, filters, page, index, productsPerPage } = event.detail;
                console.log('Search', 'Request for search', productsPerPage);

                if (this.isProcessingRequest) {
                    this.cancelCurrentRequest();
                }
                this.isProcessingRequest = true;
                this.requestType = type;

                if (this.requestType === 'search') {
                    window.dispatchEvent(new CustomEvent('show-spinner', {
                        detail: {
                            id: 'spinner-search',
                        },
                    }));
                } else if (this.requestType === 'plp') {
                    window.dispatchEvent(new CustomEvent('show-spinner', {
                        detail: {
                            id: 'spinner-collection',
                        },
                    }));
                }

                if (this.requestType === 'header') {
                    defaultSearchParams.hitsPerPage = this.headerResultsTotal;
                } else {
                    defaultSearchParams.hitsPerPage = productsPerPage
                        ? productsPerPage
                        : this.productsPerPageSetting;
                }

                const searchIndex = this.config.index[index] ? index : this.defaultSort;

                const customSearchParams = this.buildCustomSearchParams(filters, page);
                const results = await this.algoliaProductSearch(query, customSearchParams, {}, searchIndex);
            }
        };
    },
};

export default algolia;